// light-mask.service.ts
import { Injectable } from '@angular/core';
import { Container, Graphics, BlurFilter } from 'pixi.js';

export interface LightSource {
  x: number;
  y: number;
  type: 'circle' | 'ellipse' | 'rect';
  radius?: number;
  width?: number;
  height?: number;
  blur?: number;
  intensity?: number;
}

export interface LightMaskOptions {
  width: number;
  height: number;
  darkAlpha?: number;
  backgroundColor?: number;
  lights: LightSource[];
}

export interface LightMaskContainer {
  container: Container;
  darkOverlay: Graphics;
  lightMask: Graphics;
  updateLights: (lights: LightSource[]) => void;
  updatePosition: (x: number, y: number, lightIndex?: number) => void;
  setDarkness: (alpha: number) => void;
  destroy: () => void;
}

@Injectable({
  providedIn: 'root'
})
export class LightMaskService {
  
  createLightMask(options: LightMaskOptions): LightMaskContainer {
    const {
      width,
      height,
      darkAlpha = 0.7,
      backgroundColor = 0x000000,
      lights
    } = options;

    const container = new Container();
    
    // Создаем слой "тьмы"
    const darkOverlay = new Graphics();
    darkOverlay.rect(0, 0, width, height).fill({ color: backgroundColor, alpha: darkAlpha });
    
    // Создаем маску
    const lightMask = new Graphics();
    
    // Функция отрисовки маски
    const drawLightsOnMask = () => {
      lightMask.clear();
      
      // 🔥 ВАЖНО: Начинаем с ЧЁРНОГО фона (вся область затемнена)
      lightMask.rect(0, 0, width, height).fill({ color: 0x000000 });
      
      // 🔥 Рисуем БЕЛЫЕ области для источников света (здесь тени не будет)
      lights.forEach(light => {
        const intensity = light.intensity ?? 1;
        // Для маски используем белый цвет (показывает содержимое darkOverlay)
        // Интенсивность влияет на прозрачность тени
        const alpha = intensity; // белый с разной прозрачностью
        
        switch (light.type) {
          case 'circle':
            if (light.radius) {
              lightMask.circle(light.x, light.y, light.radius).fill({ color: 0xffffff, alpha });
            }
            break;
          case 'ellipse':
            if (light.width && light.height) {
              lightMask.ellipse(light.x, light.y, light.width, light.height).fill({ color: 0xffffff, alpha });
            }
            break;
          case 'rect':
            if (light.width && light.height) {
              lightMask.rect(
                light.x - light.width / 2,
                light.y - light.height / 2,
                light.width,
                light.height
              ).fill({ color: 0xffffff, alpha });
            }
            break;
        }
      });
    };
    
    // Рисуем маску
    drawLightsOnMask();
    
    // Добавляем в контейнер
    container.addChild(darkOverlay);
    
    // Применяем маску
    darkOverlay.mask = lightMask;
    
    // Для отладки: сделаем маску видимой (временно)
    // lightMask.visible = true; // можно включить для проверки
    
    return {
      container,
      darkOverlay,
      lightMask,
      
      updateLights: (newLights: LightSource[]) => {
        lights.length = 0;
        lights.push(...newLights);
        drawLightsOnMask();
        
        if (darkOverlay.parent) {
          darkOverlay.mask = lightMask;
        }
      },
      
      updatePosition: (x: number, y: number, lightIndex: number = 0) => {
        if (lights[lightIndex]) {
          lights[lightIndex].x = x;
          lights[lightIndex].y = y;
          drawLightsOnMask();
          
          if (darkOverlay.parent) {
            darkOverlay.mask = lightMask;
          }
        }
      },
      
      setDarkness: (alpha: number) => {
        darkOverlay.clear();
        darkOverlay.rect(0, 0, width, height).fill({ 
          color: backgroundColor, 
          alpha: Math.max(0, Math.min(1, alpha))
        });
        
        if (darkOverlay.parent) {
          darkOverlay.mask = lightMask;
        }
      },
      
      destroy: () => {
        if (darkOverlay) {
          darkOverlay.mask = null;
        }
        lightMask.destroy();
        darkOverlay.destroy();
        container.destroy();
      }
    };
  }
  
  private getIntensityColor(intensity: number): number {
    const value = Math.floor(255 * intensity);
    return (value << 16) | (value << 8) | value;
  }
}